import {
  Box,
  Button,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { CardTitle } from "@ssf/components";
import type { Club, Registration } from "@ssf/memdb-api";
import { MembershipType, TmpClub } from "@ssf/memdb-api";
import { MembershipShield } from "@ssf/memdb-components";
import {
  ShieldAccount,
  UnfoldLessHorizontal,
  UnfoldMoreHorizontal,
} from "mdi-material-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { generatePath } from "../lib/index.js";

type PickedRegistration = Pick<
  Registration,
  "clubid" | "date" | "membershiptype" | "documentid"
> & {
  club: Pick<Club, "name">;
};

const currentYear = new Date().getFullYear();

const SecondaryAction = ({
  memberId,
  registration,
}: {
  memberId: number;
  registration: PickedRegistration;
}) => {
  const { t } = useTranslation("MemberRegistration");

  if (registration.membershiptype < MembershipType.NoneOfficialMember) {
    return null;
  }

  return (
    <Button
      component={Link}
      to={generatePath("signMembership", {
        clubId: registration.clubid.toString(),
        memberId: memberId.toString(),
      })}
      variant="outlined"
      color="inherit"
      size="small"
      sx={{ ml: 1.5 }}
    >
      {t("sign")}
    </Button>
  );
};

export const MemberRegistrations = ({
  memberId,
  registrations,
  disableSecondaryAction,
}: {
  memberId: number;
  registrations: PickedRegistration[];
  disableSecondaryAction?: boolean;
}) => {
  const { t } = useTranslation("MemberRegistration");

  const [isExpanded, setExpanded] = useState(false);
  const groupedRegistrations = useMemo(() => {
    const years: Record<number, typeof registrations> = {};
    registrations.forEach((registration) => {
      const year = new Date(registration.date).getFullYear();
      if (
        registration.clubid !== TmpClub &&
        (isExpanded || year >= currentYear - 3) &&
        (registration.membershiptype < MembershipType.NoneOfficialMember ||
          year >= currentYear)
      ) {
        years[year] = [registration, ...(years[year] || [])];
      }
    });

    return Object.values(years).reverse();
  }, [registrations, isExpanded]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <CardTitle icon={<ShieldAccount />} title={t("title")} />
        <Tooltip title={t(isExpanded ? "collapse" : "expand")}>
          <IconButton onClick={() => setExpanded(!isExpanded)}>
            {isExpanded ? <UnfoldLessHorizontal /> : <UnfoldMoreHorizontal />}
          </IconButton>
        </Tooltip>
      </Box>
      {groupedRegistrations.map((registrations, index) => (
        <Paper key={index.toString()} sx={[{ py: 1 }, index ? { mt: 1 } : {}]}>
          {registrations.map((registration) => (
            <ListItem
              key={`${registration.clubid}-${registration.date}-${registration.membershiptype}`}
              dense
              disablePadding
              secondaryAction={
                !disableSecondaryAction && (
                  <SecondaryAction
                    memberId={memberId}
                    registration={registration}
                  />
                )
              }
              sx={[
                ({ typography }) => typography.body2,
                { "> .MuiListItemButton-root": { pr: 2 } },
              ]}
            >
              <ListItemButton
                component={Link}
                to={generatePath("members", {
                  clubId: registration.clubid.toString(),
                })}
              >
                <MembershipShield {...registration} />
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  sx={{ ml: 1 }}
                >
                  {registration.club.name}
                </ListItemText>
                {(registration.documentid ||
                  registration.membershiptype <
                    MembershipType.NoneOfficialMember) && (
                  <Typography variant="body2">{registration.date}</Typography>
                )}
                {!disableSecondaryAction && (
                  <Box sx={{ visibility: "hidden", pointerEvents: "none" }}>
                    <SecondaryAction
                      memberId={memberId}
                      registration={registration}
                    />
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </Paper>
      ))}
    </>
  );
};
